/**
 * Folds a string into a comparable search key that works across French and
 * Arabic input. Latin accents are stripped (so "Bechar" matches "Béchar"),
 * Arabic harakat/tatweel are removed and the common letter variants are folded
 * (so "امل" matches "أمل"), and Arabic-Indic digits become Latin digits (so
 * "20" matches "٢٠").
 */
const ARABIC_INDIC = '٠١٢٣٤٥٦٧٨٩';
const EASTERN_ARABIC_INDIC = '۰۱۲۳۴۵۶۷۸۹';

export function foldSearchText(input: string | null | undefined): string {
  if (!input) return '';
  let out = input.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  // Remove harakat (U+064B–U+0652), additional marks (U+0653–U+0655), tatweel (U+0640), superscript alef (U+0670)
  // Note: original brief regex was too broad and included digit codepoints
  out = out.replace(/[ً-ٕـٰ]/g, '');
  out = out.replace(/[أإآٱ]/g, 'ا'); // أ إ آ ٱ -> ا
  out = out.replace(/ى/g, 'ي'); // ى -> ي
  out = out.replace(/ة/g, 'ه'); // ة -> ه
  out = out.replace(/[ؤئ]/g, 'ء'); // ؤ ئ -> ء
  out = out.replace(/[٠-٩۰-۹]/g, (d) => {
    const i = ARABIC_INDIC.indexOf(d);
    return String(i >= 0 ? i : EASTERN_ARABIC_INDIC.indexOf(d));
  });
  return out.toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * PostgREST `ilike` pattern for a team name typed in French or Arabic: the
 * Arabic letters that are often written interchangeably (أ/إ/آ/ا, ى/ي, ة/ه)
 * become the `_` single-character wildcard, harakat and tatweel are dropped.
 */
export function buildTeamSearchPattern(query: string): string {
  const folded = query
    .trim()
    .replace(/[%]/g, '') // SQL multi-char wildcard handled by us
    .replace(/[ً-ْٰـ]/g, '') // harakat + tatweel
    .replace(/[أإآا]/g, '_')
    .replace(/[ىي]/g, '_')
    .replace(/[ةه]/g, '_');
  return `%${folded}%`;
}

/**
 * Identity key of a club name: folded, the Arabic article « ال » dropped from
 * each word, spaces and punctuation removed. « CMTidjelabine » = « CM
 * Tidjelabine », « شباب تيبازة » = « شباب  تيبازه ». Two names with the same key
 * are the same club as typed by two different people.
 */
export function teamNameKey(input: string | null | undefined): string {
  return foldSearchText(input)
    .replace(/(^|\s)ال/g, '$1')
    .replace(/[^\p{L}\p{N}]+/gu, '');
}
